<template>
  <b-modal
    :visible="showQrCodeModal"
    size="xl"
    centered
    scrollable
    hide-footer
    @hidden="closeModal"
  >
    <template #modal-header="{ close }">
      <div class="d-flex align-items-start w-100">
        <button
          v-if="selected"
          type="button"
          class="btn btn-link text-primary px-0 mr-2"
          title="Voltar"
          @click="selected = null"
        >
          <i class="fas fa-arrow-left"></i>
        </button>
        <div class="flex-grow-1 min-w-0">
          <h2 class="mb-1 h3">{{ headerTitle }}</h2>
          <p class="mb-0 text-muted">{{ productLine }}</p>
        </div>
        <button type="button" class="close ml-2" aria-label="Fechar" @click="close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </template>

    <b-row v-if="!selected">
      <b-col
        v-for="option in options"
        :key="option.key"
        cols="12"
        md="6"
        class="mb-3"
      >
        <product-qr-code-option-card
          :icon="option.icon"
          :title="option.title"
          :text="option.managerText"
          :disabled="isQuotationOptionDisabled(option)"
          @select="selectOption(option)"
        />
      </b-col>
    </b-row>

    <div v-else class="text-center">
      <div id="qr-code" class="d-inline-block">
        <qrcode-vue
          v-if="expressBookingURL"
          render-as="svg"
          :size="qrSize"
          :value="expressBookingURL"
        />
      </div>

      <div class="d-flex flex-wrap justify-content-center qr-code-actions mt-4">
        <button type="button" class="btn btn-primary" @click="copyTextToClipboard">
          <i class="fas fa-copy mr-2"></i>Copiar link
        </button>
        <button type="button" class="btn btn-primary" @click="openQrCodeNewTab">
          <i class="fas fa-share mr-2"></i>Abrir link
        </button>
        <button type="button" class="btn btn-primary" @click="downloadQrCode">
          <i class="fas fa-download mr-2"></i>Baixar imagem
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="printingPdf"
          @click="downloadQrCodePdf"
        >
          <i class="fas fa-file-pdf mr-2"></i>
          {{ printingPdf ? 'Gerando PDF...' : 'Baixar PDF' }}
        </button>
      </div>
    </div>

    <div class="qr-print-source" aria-hidden="true">
      <qrcode-vue
        v-if="selected && expressBookingURL"
        ref="printQr"
        render-as="canvas"
        :size="600"
        :value="expressBookingURL"
      />
    </div>
  </b-modal>
</template>

<script>
import qrcodeVue from 'qrcode.vue'
import { toPng } from 'html-to-image'
import { jsPDF } from 'jspdf'
import { mapGetters } from 'vuex'
import { loadPublicLogoUrl } from '@/store/utils/publicLogoFile'
import ProductQrCodeOptionCard from './ProductQrCodeOptionCard.vue'

const QR_OPTIONS = [
  {
    key: 'express',
    icon: 'fas fa-qrcode',
    title: 'Reserva expressa',
    managerText:
      'Venda direta de reservas. Vagas e calendário seguem a capacidade do atrativo. O pagamento é por Pix.',
    filename: 'Reserva expressa',
    visitor: {
      headline: 'Reserve aqui',
      lead: 'Escolha a data, confira as vagas e pague sua reserva por Pix.',
      steps: [
        'Aponte a câmera do celular para o código.',
        'Escolha o dia e a quantidade de pessoas.',
        'Conclua o pagamento por Pix.',
      ],
    },
  },
  {
    key: 'auto_checkin',
    icon: 'fas fa-bolt',
    title: 'Auto check-in',
    managerText:
      'Compra só para o mesmo dia, sem calendário. O check-in é registrado na hora e substitui o livro de ata na entrada.',
    filename: 'Auto check-in',
    visitor: {
      headline: 'Registre sua entrada aqui',
      lead: 'Válido para hoje. O check-in acontece no momento da compra.',
      steps: [
        'Aponte a câmera do celular para o código.',
        'Informe seus dados.',
        'Confirme. Sua entrada fica registrada na hora.',
      ],
    },
  },
  {
    key: 'paylater',
    icon: 'fas fa-clock',
    title: 'Cotação',
    managerText:
      'Cria a reserva sem pagamento. O funil segue Cotação (sem pagamento), Pré-reserva (aprovada, ainda sem pagamento) e Reserva (com pagamento aprovado).',
    filename: 'Cotação',
    visitor: {
      headline: 'Peça sua cotação',
      lead: 'Solicite a reserva agora. O pagamento é combinado depois.',
      steps: [
        'Aponte a câmera do celular para o código.',
        'Preencha os dados da visita.',
        'Envie o pedido. Nossa equipe confirma a disponibilidade.',
      ],
    },
  },
  {
    key: 'nps',
    icon: 'fas fa-smile',
    title: 'Pesquisa de satisfação',
    managerText:
      'Leva à pesquisa, não à venda. Imprima onde os visitantes se concentram: a mensagem automática vai para um único telefone da reserva.',
    filename: 'Pesquisa de satisfação',
    visitor: {
      headline: 'Como foi sua visita?',
      lead: 'Sua opinião ajuda a melhorar a experiência. Leva menos de um minuto.',
      steps: [
        'Aponte a câmera do celular para o código.',
        'Responda as perguntas.',
        'Envie. Obrigado por participar.',
      ],
    },
  },
]

export default {
  name: 'ProductQrCode',

  components: {
    qrcodeVue,
    ProductQrCodeOptionCard,
  },

  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
    showQrCodeModal: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      options: QR_OPTIONS,
      selected: null,
      placeLogo: '',
      placeLogoObjectUrl: '',
      printingPdf: false,
    }
  },

  computed: {
    ...mapGetters('auth', {
      place: 'getPlace',
    }),

    placeName() {
      return (this.place && (this.place.name || this.place.place_name)) || ''
    },

    productLine() {
      const placePart = this.product.vendor_name
        ? this.product.product_place_name
        : ''
      const name = this.product.product_name || ''
      return placePart ? `${placePart} - ${name}` : name
    },

    headerTitle() {
      return this.selected ? this.selected.title : 'QR Codes'
    },

    qrSize() {
      if (typeof window === 'undefined') return 280
      return window.innerWidth < 576 ? 220 : 280
    },

    activeProduct() {
      const product = { ...this.product }
      const key = this.selected && this.selected.key
      product.auto_checkin = key === 'auto_checkin'
      product.paylater = key === 'paylater'
      product.nps = key === 'nps'
      return product
    },

    expressBookingParam() {
      const PLACE_UUID = this.place.uuid || this.place.janooUserUuid
      const params = JSON.stringify({
        place_uuid: PLACE_UUID,
        product_uuid: this.product.uuid,
        auto_checkin: this.activeProduct.auto_checkin || false,
        paylater: this.activeProduct.paylater || false,
      })
      return btoa(params)
    },

    expressBookingURL() {
      if (!this.selected) return ''
      if (this.activeProduct.nps) return this.buildSurveyUrl(this.product)
      return window.location.origin + '/booking/?' + this.expressBookingParam
    },

    selectedVisitor() {
      return (
        (this.selected && this.selected.visitor) || {
          headline: '',
          lead: '',
          steps: [],
        }
      )
    },
  },

  watch: {
    showQrCodeModal(visible) {
      if (visible) {
        this.selected = null
        this.loadPlaceLogo()
      }
    },
  },

  methods: {
    isQuotationOptionDisabled(option) {
      return option && option.key === 'paylater' && this.product.quotationsale == false
    },

    selectOption(option) {
      if (this.isQuotationOptionDisabled(option)) return
      this.selected = option
    },

    async loadPlaceLogo() {
      this.clearPlaceLogo()
      try {
        const loaded = await loadPublicLogoUrl(this.place)
        if (!loaded.url) return
        if (loaded.owned) this.placeLogoObjectUrl = loaded.url
        this.placeLogo = loaded.url
      } catch (error) {
        this.placeLogo = ''
      }
    },

    clearPlaceLogo() {
      if (this.placeLogoObjectUrl) {
        URL.revokeObjectURL(this.placeLogoObjectUrl)
      }
      this.placeLogoObjectUrl = ''
      this.placeLogo = ''
    },

    closeModal() {
      this.selected = null
      this.clearPlaceLogo()
      this.$emit('closeProductQrCodeModal')
    },

    systemBaseUrl() {
      return (
        process.env.VUE_APP_SYSTEM_BASE_URL ||
        window.location.origin ||
        ''
      ).replace(/\/$/, '')
    },

    extractCollectorIdFromLink(link) {
      const value = String(link || '').trim()
      if (!value) return ''

      const surveyMatch = value.match(
        /\/survey\/[^/?#]+\/([A-Za-z0-9_-]+)\/?/i
      )
      if (surveyMatch && surveyMatch[1]) return surveyMatch[1]

      const zsMatch = value.match(/\/zs\/([A-Za-z0-9_-]+)/i)
      if (zsMatch && zsMatch[1]) return zsMatch[1]

      if (/^[A-Za-z0-9_-]{2,32}$/.test(value) && value.indexOf('/') < 0) {
        return value
      }

      return ''
    },

    resolveProductNpsLink(product) {
      const item = product || {}
      const candidates = [item.product_nps, item.nps_link, item.npsLink]

      let xfields = item.xfields
      if (typeof xfields === 'string') {
        try {
          xfields = JSON.parse(xfields)
        } catch (error) {
          xfields = null
        }
      }

      if (xfields) {
        if (xfields.nps_link) {
          candidates.push(
            xfields.nps_link.value,
            xfields.nps_link.label,
            typeof xfields.nps_link === 'string' ? xfields.nps_link : null
          )
        }

        const laravelData = xfields.laravel_data
        if (laravelData) {
          const npsFromLaravel =
            (laravelData.nps_link &&
              (laravelData.nps_link.value || laravelData.nps_link)) ||
            laravelData.product_nps ||
            null
          if (npsFromLaravel) candidates.push(npsFromLaravel)
        }
      }

      return candidates.find((entry) => entry && String(entry).trim()) || ''
    },

    resolveSurveyCollectorId(product) {
      const item = product || {}
      const fromLink = this.extractCollectorIdFromLink(
        this.resolveProductNpsLink(item)
      )
      if (fromLink) return fromLink

      const productId =
        item.id != null
          ? item.id
          : item.product_id != null
          ? item.product_id
          : null

      if (productId == null || productId === '') return 'INTERNAL_0'
      return `INTERNAL_${productId}`
    },

    buildSurveyUrl(product) {
      const item = product || {}
      const productUuid = item.uuid || ''
      const collectorId = this.resolveSurveyCollectorId(item)
      return `${this.systemBaseUrl()}/survey/${productUuid}/${collectorId}`
    },

    copyTextToClipboard() {
      if (!navigator.clipboard) {
        this.$notify({
          message: 'Erro ao copiar o link para área de transferência',
          duration: 6000,
          type: 'error',
        })
        return
      }

      navigator.clipboard.writeText(this.expressBookingURL).then(() => {
        this.$notify({
          message: 'Link copiado!',
          duration: 5000,
          type: 'default',
        })
      })
    },

    openQrCodeNewTab() {
      window.open(this.expressBookingURL, '_blank')
    },

    downloadQrCode() {
      const label = (this.selected && this.selected.filename) || 'QR Code'
      const filename = `QR Code - ${label} - ${this.product.product_name || 'atrativo'}.png`
      toPng(document.getElementById('qr-code')).then((dataUrl) => {
        const link = document.createElement('a')
        link.setAttribute('href', dataUrl)
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
    },

    async downloadQrCodePdf() {
      if (this.printingPdf || !this.selected) return
      this.printingPdf = true
      const label = this.selected.filename || 'QR Code'
      const filename = `QR Code - ${label} - ${
        this.product.product_name || 'atrativo'
      }.pdf`
      try {
        await this.$nextTick()
        const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
        const pageW = 210
        const margin = 18
        const contentW = pageW - margin * 2
        const navy = [18, 87, 110]
        const muted = [82, 95, 127]
        let y = 22

        const logoData = await this.logoDataUrl()
        if (logoData) {
          const logoSize = 22
          doc.addImage(
            logoData,
            'PNG',
            (pageW - logoSize) / 2,
            y,
            logoSize,
            logoSize
          )
          y += logoSize + 8
        }

        doc.setTextColor(...navy)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(20)
        y = this.writeCentered(doc, this.placeName || ' ', y, contentW, margin, 8)
        y += 2

        doc.setFont('helvetica', 'normal')
        doc.setFontSize(12)
        doc.setTextColor(...muted)
        y = this.writeCentered(doc, this.productLine || ' ', y, contentW, margin, 6)
        y += 10

        doc.setDrawColor(226, 232, 235)
        doc.setLineWidth(0.3)
        doc.line(margin, y, pageW - margin, y)
        y += 14

        doc.setTextColor(...navy)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(26)
        y = this.writeCentered(
          doc,
          this.selectedVisitor.headline,
          y,
          contentW,
          margin,
          10
        )
        y += 3

        doc.setFont('helvetica', 'normal')
        doc.setFontSize(13)
        doc.setTextColor(50, 50, 93)
        y = this.writeCentered(
          doc,
          this.selectedVisitor.lead,
          y,
          contentW - 20,
          margin + 10,
          7
        )
        y += 8

        const printRoot = this.$refs.printQr && this.$refs.printQr.$el
        const qrCanvas =
          printRoot &&
          (printRoot.tagName === 'CANVAS'
            ? printRoot
            : printRoot.querySelector('canvas'))
        if (qrCanvas && qrCanvas.toDataURL) {
          const qrSize = 78
          doc.addImage(
            qrCanvas.toDataURL('image/png'),
            'PNG',
            (pageW - qrSize) / 2,
            y,
            qrSize,
            qrSize
          )
          y += qrSize + 14
        }

        const steps = this.selectedVisitor.steps || []
        steps.forEach((step, index) => {
          const circleX = margin + 5
          doc.setFillColor(...navy)
          doc.circle(circleX, y + 1.5, 4, 'F')
          doc.setTextColor(255, 255, 255)
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(11)
          doc.text(String(index + 1), circleX, y + 2.8, { align: 'center' })

          doc.setTextColor(31, 42, 46)
          doc.setFont('helvetica', 'normal')
          doc.setFontSize(12)
          const lines = doc.splitTextToSize(step, contentW - 16)
          doc.text(lines, margin + 14, y + 2.5)
          y += Math.max(12, lines.length * 6 + 4)
        })

        doc.save(filename)
      } catch (error) {
        this.$notify({
          message: 'Não foi possível gerar o PDF',
          duration: 6000,
          type: 'error',
        })
      } finally {
        this.printingPdf = false
      }
    },

    writeCentered(doc, text, y, width, x, lineHeight) {
      const lines = doc.splitTextToSize(String(text || ''), width)
      doc.text(lines, x + width / 2, y, { align: 'center' })
      return y + lines.length * lineHeight
    },

    logoDataUrl() {
      if (!this.placeLogo) return Promise.resolve('')
      return new Promise((resolve) => {
        const image = new Image()
        image.crossOrigin = 'anonymous'
        image.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            canvas.width = image.naturalWidth || 88
            canvas.height = image.naturalHeight || 88
            canvas.getContext('2d').drawImage(image, 0, 0)
            resolve(canvas.toDataURL('image/png'))
          } catch (error) {
            resolve('')
          }
        }
        image.onerror = () => resolve('')
        image.src = this.placeLogo
      })
    },
  },

  mounted() {
    this.loadPlaceLogo()
  },
}
</script>

<style>
.qr-code-actions {
  gap: 0.5rem;
}

.qr-code-actions .btn {
  margin: 0.15rem;
}

.qr-print-source {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
}
</style>
